import { useEffect, useId, useState } from 'react'
import { apiPlataforma, type ResumenDeCargaDeCatalogo } from '../../servicios/apiPlataforma'
import { ErrorDeApi, type ResumenDeCatalogo } from '../../servicios/api'
import { useTrampaDeFoco } from '../../ganchos/useTrampaDeFoco'
import { telon } from '../../ganchos/telon'

const numero = (n: number) => n.toLocaleString('es-MX')

/** `ultimaCargaUtc` es un instante, no una fecha contable: aquí sí se convierte a hora local (ver fechas.ts). */
const instante = (iso: string) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })

/**
 * Los mismos topes que `ServicioDeCargaDeCatalogo.MaxVersion` y `MaxOrigen`, que los hace
 * cumplir antes de leer el archivo: `VersionCatalogo.Version` es VARCHAR(40), y `Origen` es
 * NVARCHAR(500) al que se le agrega « · SHA-256 <16 hex>…» (28 caracteres). Repetirlos aquí
 * solo evita que la persona espere la subida para enterarse.
 */
const MAX_VERSION = 40
const MAX_ORIGEN = 472

/**
 * Carga de los catálogos nacionales del SAT (RF-1003, ADR-014). Vive en la consola de
 * plataforma y no en la del despacho: un catálogo del SAT es la base contra la que se validan
 * los comprobantes de TODOS los despachos, y ninguno debe poder reescribírselo a los demás.
 *
 * Hasta ahora el endpoint existía sin pantalla, así que «cargarlo desde la consola» exigía
 * sacar el token del navegador y armar un multipart a mano.
 */
export function CatalogosDePlataforma() {
  const idTitulo = useId()
  const [catalogos, setCatalogos] = useState<ResumenDeCatalogo[] | null>(null)
  const [error, setError] = useState('')
  const [cargandoA, setCargandoA] = useState<ResumenDeCatalogo | null>(null)

  async function recargar() {
    try {
      setCatalogos(await apiPlataforma.estadoDeCatalogos())
      setError('')
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo consultar el estado de los catálogos.')
    }
  }

  useEffect(() => {
    void recargar()
  }, [])

  return (
    <section className="seccion" aria-labelledby={idTitulo}>
      <h2 id={idTitulo}>Catálogos del SAT</h2>
      <p className="sutil">
        Las claves contra las que se validan los comprobantes de todos los despachos. Una versión
        nueva del SAT es una carga, no un despliegue. Las claves que ya no vengan en el archivo se
        vencen, nunca se borran (RN-09).
      </p>

      {error && <p className="error" role="alert">{error}</p>}
      {!catalogos && !error && <p className="vacio">Cargando…</p>}

      {catalogos && (
        <div className="tabla-envoltura">
          <table className="tabla">
            <thead>
              <tr>
                <th scope="col">Catálogo</th>
                <th scope="col">Estado</th>
                <th scope="col" className="cupo">Claves vigentes</th>
                <th scope="col">Versión</th>
                <th scope="col">Última carga</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {catalogos.map((c) => (
                <tr key={c.catalogo}>
                  <th scope="row" className="mono">{c.catalogo}</th>
                  <td>
                    <span className={`insignia ${c.estado === 'Cargado' ? 'vigente' : 'vencido'}`}>
                      {c.estado === 'Cargado' ? 'Cargado' : 'No cargado'}
                    </span>
                  </td>
                  {/* Sin cargar no es «0 claves»: es que no hay dato. */}
                  <td className="cupo">{c.estado === 'Cargado' ? numero(c.clavesVigentes) : '—'}</td>
                  <td>{c.version ?? '—'}</td>
                  <td className="sutil">
                    {c.ultimaCargaUtc ? instante(c.ultimaCargaUtc) : 'Nunca'}
                    {c.ultimaCargaError && <> · {c.ultimaCargaError}</>}
                  </td>
                  <td>
                    <button className="enlace" onClick={() => setCargandoA(c)}>Cargar CSV</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cargandoA && (
        <CargaDeCatalogoModal
          catalogo={cargandoA}
          onCerrar={() => {
            setCargandoA(null)
            void recargar()
          }}
        />
      )}
    </section>
  )
}

function CargaDeCatalogoModal({ catalogo, onCerrar }: { catalogo: ResumenDeCatalogo; onCerrar: () => void }) {
  const idTitulo = useId()
  const [enviando, setEnviando] = useState(false)
  // Mientras sube no se cierra: el API sigue procesando aunque el modal desaparezca, y
  // volver a pulsar «Cargar» mandaría el mismo archivo otra vez.
  const cerrar = () => {
    if (!enviando) onCerrar()
  }
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, cerrar)
  const [error, setError] = useState('')
  const [resumen, setResumen] = useState<ResumenDeCargaDeCatalogo | null>(null)

  async function cargar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const datos = new FormData(evento.currentTarget)
    // Del input y no de FormData, igual que RepositorioCfdi: es la lista de archivos que de
    // verdad eligió la persona.
    const archivo = (evento.currentTarget.elements.namedItem('archivo') as HTMLInputElement).files?.[0]
    if (!archivo || archivo.size === 0) {
      setError('Elige el archivo CSV del catálogo.')
      return
    }

    const publicadoPorSat = String(datos.get('publicadoPorSat') ?? '')
    setError('')
    setEnviando(true)
    try {
      setResumen(await apiPlataforma.cargarCatalogo(catalogo.catalogo, {
        archivo,
        version: String(datos.get('version')).trim(),
        origen: String(datos.get('origen')).trim(),
        ...(publicadoPorSat && { publicadoPorSat }),
      }))
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar el catálogo.')
    } finally {
      setEnviando(false)
    }
  }

  if (resumen) {
    return (
      <div className="modal" {...telon(() => onCerrar())}>
        <div role="dialog" aria-modal="true" aria-labelledby={idTitulo} className="panel" onClick={(e) => e.stopPropagation()}>
          <h2 id={idTitulo}>{TITULO_DEL_RESULTADO[resumen.estado]} · <span className="mono">{resumen.catalogo}</span></h2>
          {resumen.estado === 'SinCambios' ? (
            <p className="sutil">
              El archivo es idéntico, byte por byte, al de la última carga buena: no se reprocesó nada.
            </p>
          ) : (
            <dl className="datos">
              <dt>Filas leídas</dt><dd>{numero(resumen.filas)}</dd>
              <dt>Nuevas</dt><dd>{numero(resumen.nuevas)}</dd>
              <dt>Actualizadas</dt><dd>{numero(resumen.actualizadas)}</dd>
              <dt>Vencidas</dt><dd>{numero(resumen.vencidas)}</dd>
              <dt>Con error</dt><dd>{numero(resumen.conError)}</dd>
            </dl>
          )}
          {resumen.errores.length > 0 && (
            <>
              <p className="error" role="alert">
                {numero(resumen.conError)} renglón(es) no entraron. Corrígelos en el CSV y vuelve a
                cargarlo; los buenos ya quedaron guardados. Los primeros:
              </p>
              <ul>
                {resumen.errores.map((e) => <li key={e} className="mono">{e}</li>)}
              </ul>
            </>
          )}
          <div className="acciones">
            <button className="primario" onClick={onCerrar}>Entendido</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="modal" {...telon(cerrar)}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={cargar}
      >
        <h2 id={idTitulo}>Cargar <span className="mono">{catalogo.catalogo}</span></h2>
        <p className="sutil">
          CSV normalizado con el encabezado exacto (ver <span className="mono">docs/OPERACION_CATALOGOS.md</span>).
          Subir dos veces el mismo archivo no reprocesa nada.
        </p>

        <label>
          Archivo CSV
          <input name="archivo" type="file" accept=".csv,text/csv" required disabled={enviando} />
        </label>

        <label>
          Versión
          <input name="version" required maxLength={MAX_VERSION} disabled={enviando} placeholder="catCFDI_V_4_20260925" />
        </label>

        <label>
          Procedencia
          <input
            name="origen"
            required
            maxLength={MAX_ORIGEN}
            disabled={enviando}
            placeholder="Libro catCFDI del SAT convertido con convertir-catalogos-cfdi.py"
          />
        </label>
        <p className="sutil">De dónde salió el archivo, en tus palabras. Queda junto con su huella SHA-256.</p>

        <label>
          Publicado por el SAT (opcional)
          <input name="publicadoPorSat" type="date" disabled={enviando} />
        </label>
        <p className="sutil">Déjalo vacío si no lo sabes con certeza: mejor sin fecha que con una supuesta.</p>

        {enviando && (
          <p className="aviso" role="status">
            Cargando… con c_ClaveProdServ (más de 50 mil claves) puede tardar varios minutos. No cierres esta ventana.
          </p>
        )}
        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={cerrar} disabled={enviando}>Cancelar</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Cargando…' : 'Cargar'}
          </button>
        </div>
      </form>
    </div>
  )
}

const TITULO_DEL_RESULTADO: Record<ResumenDeCargaDeCatalogo['estado'], string> = {
  Terminada: 'Catálogo cargado',
  SinCambios: 'Sin cambios',
  Error: 'La carga falló',
  EnProceso: 'Carga en proceso',
}
